import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import get from 'lodash.get';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  selectPageIndex,
  setPageIndex,
} from '../../features/pageIndex/pageIndexSlice';
import {
  selectPageSizeX,
  setPageSizeX,
} from '../../features/pageSize/pageSizeXSlice';
import { supabase } from '../../utils/supabase';
import PostcodeSearch from '../input/PostcodeSearch';
import FilterModal from './FilterModal';
import ListCards from './ListCards';

function FilterPane({ data, columns, defaultCountry }) {
  const location = useLocation();
  const currentPage = useSelector(selectPageIndex);
  const dispatch = useDispatch();
  const pageSizeX = useSelector(selectPageSizeX);
  const defaultPageSizeX = location.pathname === "/" ? 5 : pageSizeX;
  const [cardsPerPage, setCardsPerPageState] = useState(defaultPageSizeX);
  const [sortedData, setSortedData] = useState(data);
  const [filteredData, setFilteredData] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [orderedData, setOrderedData] = useState(null); // for postcode search order
  const [postcode, setPostcode] = useState(null);
  const [courseImages, setCourseImages] = useState([]);
  const [detailsVisibility, setDetailsVisibility] = useState({});
  const [postcodeCoords, setPostcodeCoords] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => setIsModalOpen(!isModalOpen);
  const [showQuickFilters, setShowQuickFilters] = useState(true);
  const [countyOptions, setCountyOptions] = useState([]);
  const [isCountyOptionsLoading, setIsCountyOptionsLoading] = useState(false);

  //console.log("columns parent", columns)
  //console.log("Details Vis Home", detailsVisibility);

  const isImageAvailable = (courseName) => {
    const courseImage = courseImages.find(
      (image) => image.course_name === courseName,
    );
    return !!courseImage && !!courseImage.image_url; // Check if courseImage and its image_url exist
  };

  //This updated function first creates a new state object with all course details set to hidden (false).
  //Then, it toggles the visibility for the specific course that was clicked.This approach ensures that
  //when a user clicks to show more details for a course, any previously shown details for other courses are hidden,
  //thus maintaining only one course's details open at a time.
  const toggleDetailsVisibility = (courseId) => {
    console.log("Toggling visibility for courseId:", courseId); // Add this line
    setDetailsVisibility((prevState) => {
      const updatedState = Object.keys(prevState).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      updatedState[courseId] = !prevState[courseId];
      return updatedState;
    });
  };

  const handleCoordinates = (latitude, longitude) => {
    setPostcodeCoords({ latitude, longitude });
  };

  // Define the array of column names you want to display.
  const allowedColumns = [
    "Course",
    "Country",
    "County",
    "Style",
    "Original Designer(s)",
    "Design Contributors",
    "Play Format",
    "Domestic Reciprocals",
  ];
  const allowedSearchColumns = columns.filter((column) =>
    allowedColumns.includes(column.Header),
  );

  const totalPages = Math.ceil(sortedData.length / cardsPerPage);

  const changePage = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      dispatch(setPageIndex(newPage));
    }
  };

  const handleChangeCardsPerPage = (e) => {
    const newPageSizeX = parseInt(e.target.value);
    setCardsPerPageState(newPageSizeX);
    dispatch(setPageSizeX(newPageSizeX));
    dispatch(setPageIndex(0));

    if (postcode) {
      setOrderedData(null);
      setPostcode(null);
      alert(
        "Please apply your text filters and select the number of records you want to show, before you perform postcode search.",
      );
    }
  };

  useEffect(() => {
    const sortData = () => {
      // Check if 'open_uid' is present in any of the data objects
      const hasOpenUID = data.some((item) => "open_uid" in item);

      if (!hasOpenUID) {
        // Sort by 'Country Rank' if 'open_uid' is not present
        const nationalRankColumn = columns.find(
          (column) => column.Header === "Country Rank",
        );

        if (nationalRankColumn) {
          const getRank = (row) => {
            const accessor = nationalRankColumn.accessor;
            const value =
              typeof accessor === "function"
                ? accessor(row)
                : row[accessor];
            return value != null ? Number(value) : 999;
          };
          const sortedByRank = [...data].sort(
            (a, b) => getRank(a) - getRank(b),
          );
          setSortedData(sortedByRank);
        }
      } else {
        // If 'open_uid' is present, we keep the data order as is from the parent component
        setSortedData(data);
      }
    };

    sortData();
  }, [columns, data]);

  const [searchTerms, setSearchTerms] = useState(
    columns.reduce((acc, column) => {
      acc[column.accessor] = "";
      return acc;
    }, {}),
  );

  const selectedCountryForCounties =
    searchTerms?.course_country || defaultCountry || "";

  // Add reserved space for filter controls to prevent CLS
  // const [filterControlsHeight, setFilterControlsHeight] = useState(0);

  // Update search terms when defaultCountry changes
  useEffect(() => {
    if (defaultCountry) {
      setSearchTerms(prevTerms => ({
        ...prevTerms,
        course_country: defaultCountry
      }));
    }
  }, [defaultCountry]);

  useEffect(() => {
    const fetchCountyOptions = async () => {
      if (!selectedCountryForCounties) {
        setCountyOptions([]);
        return;
      }

      setIsCountyOptionsLoading(true);
      try {
        const { data: counties, error } = await supabase
          .from("counties")
          .select("county_name")
          .eq("country_of_county", selectedCountryForCounties)
          .order("county_name");

        if (error) throw error;
        setCountyOptions((counties || []).map((c) => c.county_name));
      } catch (error) {
        console.error("Error fetching counties:", error);
        setCountyOptions([]);
      } finally {
        setIsCountyOptionsLoading(false);
      }
    };

    fetchCountyOptions();
  }, [selectedCountryForCounties]);

  useEffect(() => {
    // Clear county selection when the country changes
    setSearchTerms((prev) => ({
      ...prev,
      course_county: "",
    }));
    dispatch(setPageIndex(0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryForCounties]);

  const clearFilters = () => {
    const resetSearchTerms = Object.keys(searchTerms).reduce((acc, key) => {
      acc[key] = ""; // Reset each search term to an empty string
      return acc;
    }, {});

    setSearchTerms(resetSearchTerms);
    dispatch(setPageIndex(0)); // Optionally reset to the first page
  };

  const handleSearchChange = (columnAccessor) => (e) => {
    if (postcode) {
      setOrderedData(null);
      setPostcode(null);
      alert(
        "Please apply your text filters and select the number of records you want to show, before you perform postcode search.",
      );
      return; // Stop further execution of the function
    }
    setSearchTerms({
      ...searchTerms,
      [columnAccessor]: e.target.value,
    });
    dispatch(setPageIndex(0)); // reset to the initial state which is 0
  };

  useEffect(() => {
  const filterData = () => {
    const filtered = sortedData.filter((item) =>
      columns.every((column) => {
        const itemData = get(item, column.accessor, ""); // use lodash get to safely access property and set default as ""
        const searchTerm = searchTerms[column.accessor];

        if (!searchTerm) {
          // if there's no search term, return true to include the item
          return true;
        }

        if (column.Header === "Country") {
          // Exact match for "Country" column
          return itemData === searchTerm;
        }

        if (column.Header === "County") {
          // Exact match for "County" column (dropdown)
          return itemData === searchTerm;
        }

        if (Array.isArray(itemData)) {
          return itemData.some((data) => {
            if (typeof data === "string" || data instanceof String) {
              return data.toLowerCase().includes(searchTerm.toLowerCase());
            }
            return false;
          });
        }

        if (typeof itemData === "string" || itemData instanceof String) {
          return itemData.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return false;
      })
    );
    setFilteredData(filtered);
  };

  filterData();
}, [sortedData, searchTerms, columns]);

  //if we have ordered data (i.e. proximity ordered) we use that
  const paginatedData = (orderedData || filteredData).slice(
    currentPage * cardsPerPage,
    (currentPage + 1) * cardsPerPage,
  );

  const getPaginatedData = () => {
    return filteredData.slice(
      currentPage * cardsPerPage,
      (currentPage + 1) * cardsPerPage,
    );
  };

  const toggleView = () => {
    setShowMap(!showMap);
    if (!showMap) {
      getPaginatedData(); //MT made a change here
    }
  };

  //make images available in child CourseImage.js
  useEffect(() => {
    const fetchImages = async () => {
      let { data, error } = await supabase
        .from("course_images")
        .select(
          "course_uid, course_name, image_url, credit, external_link, username, caption",
        );

      if (error) console.error("Error fetching course images:", error);
      if (data) setCourseImages(data);
    };

    fetchImages();
  }, []);

  //console.log("courseImages", courseImages)

  return (
    <>
      <div className="w-full grid grid-cols-1 xl:grid-cols-4 2xl:grid-cols-4 mt-0">
        <div className="col-span-1">
          <div className="block xl:hidden">
            <button
              onClick={toggleModal}
              className="fixed bottom-4 right-4 z-50 bg-gray-800 text-white px-4 py-2 rounded-xl shadow-lg border-2 border-white"
            >
              Advanced Filters
            </button>
          </div>

          {isModalOpen && (
            <FilterModal
              onClose={toggleModal}
              searchTerms={searchTerms}
              handleSearchChange={handleSearchChange}
              allowedSearchColumns={allowedSearchColumns}
              clearFilters={clearFilters}
            />
          )}

          <div className="mx-auto w-11/12 py-2 px-6 rounded-xl border bg-white shadow-md mt-2">
            <h4 className="mx-auto text-xs font-bold py-1 flex justify-between items-center">
              Quick Filters:
              <button
                onClick={() => setShowQuickFilters(!showQuickFilters)}
                className="text-xs font-semibold p-1 rounded-md focus:outline-none bg-gray-900 hover:bg-gray-500 text-white w-[100px]"
                aria-expanded={showQuickFilters}
                aria-controls="checkbox-container"
              >
                {showQuickFilters ? "Hide" : "Show"}
              </button>
            </h4>
            {showQuickFilters && (
              <>
                <p className="italic hidden xl:block text-xxs">
                  Improvements to these filters are coming soon!
                </p>

                <div className="mx-auto">
                  <button
                    className="mt-1 w-full h-10 mx-auto flex justify-center items-center text-center rounded bg-gray-800 p-4 text-white hover:bg-indigo-700 border border-white"
                    onClick={toggleView}
                  >
                    {showMap ? (
                      <>
                        <FontAwesomeIcon
                          icon={solid("list")}
                          className="mr-2 text-white"
                        />
                        <span className="xs:text-xs block">List View</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={solid("map-location-dot")}
                          className="xs:text-xs mr-2 text-white"
                        />
                        <span className="xs:text-xs block">Map View</span>
                      </>
                    )}
                  </button>
                </div>
                {/*Postcode Search Button*/}
                <div className="mx-auto">
                  <PostcodeSearch
                    visibleData={data}
                    onDataSorted={setOrderedData} //ordered data is set to sortedCourse from postcode search component
                    onPostcode={setPostcode}
                    onCoordinates={handleCoordinates}
                  />

                  {postcode && (
                    <div className="mb-1 mx-auto flex justify-between items-center w-full border border-gray-500 rounded-md">
                      <p className="px-1 text-xs flex-shrink-0">
                        Showing proximity to:{" "}
                        <span className="text-green-700 font-bold">
                          {postcode}
                        </span>
                      </p>
                      <button
                        className="ml-2 w-[88px] bg-red-500 text-white p-1 rounded hover:bg-red-700"
                        onClick={() => {
                          setOrderedData(null);
                          setPostcode(null);
                        }}
                      >
                        Reset
                      </button>
                    </div>
                  )}
                </div>
                {/*Text Inputs*/}
                <div className="mx-auto flex flex-col flex-nowrap justify-center items-center">
                  <div className="w-full flex flex-col flex-nowrap justify-start">
                    {allowedSearchColumns.slice(0, 1).map((column, index) => (
                      <div
                        key={`${column.accessor}_mobile`}
                        className="mx-auto w-full flex flex-col justify-center items-center"
                      >
                        <input
                          key={column.accessor}
                          type="text"
                          placeholder={`Search ${column.Header}`}
                          value={searchTerms[column.accessor]}
                          onChange={handleSearchChange(column.accessor)}
                          className="h-10 text-sm border rounded p-1 w-full md:w-full disabled:text-gray-500 disabled:cursor-not-allowed"
                          disabled={showMap}
                        />
                      </div>
                    ))}

                    <div className="grid grid-cols-2 gap-x-1 xl:grid-cols-1 mt-1 xl:mt-0">
                      {allowedSearchColumns.slice(1).map((column, index) => {
                        // Check if the column is "Country" or "Style" to render a select dropdown
                        if (
                          column.Header === "Country" ||
                          column.Header === "Style" ||
                          column.Header === "County"
                        ) {
                          const options =
                            column.Header === "Country"
                              ? [
                                  "England",
                                  "Northern Ireland",
                                  "Scotland",
                                  "Wales",
                                  "Ireland",
                                ]
                              : column.Header === "Style"
                                ? [
                                    "Parkland",
                                    "Links",
                                    "Heathland",
                                    "Downland",
                                    "Inland Links",
                                    "Moorland",
                                    "Forest",
                                  ]
                                : countyOptions;

                          return (
                            <div
                              key={`${column.accessor}_dropdown`}
                              className="mx-auto w-full max-w-lg flex flex-col justify-center items-center"
                            >
                              <select
                                id={column.accessor}
                                value={searchTerms[column.accessor]}
                                onChange={handleSearchChange(column.accessor)}
                                className="h-10 text-sm text-gray-500 border rounded p-1 w-full md:w-full disabled:text-gray-500 disabled:cursor-not-allowed"
                                disabled={
                                  showMap ||
                                  (column.Header === "County" &&
                                    (!selectedCountryForCounties ||
                                      isCountyOptionsLoading))
                                }
                              >
                                <option value="">
                                  {column.Header === "County"
                                    ? isCountyOptionsLoading
                                      ? "Loading counties..."
                                      : "Select County"
                                    : `Select ${column.Header}`}
                                </option>
                                {options.map((option) => {
                                  const key =
                                    typeof option === "string"
                                      ? option
                                      : String(option);
                                  return (
                                    <option key={key} value={option}>
                                      {option}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          );
                        } else {
                          // Render a text input for other columns
                          return (
                            <div
                              key={`${column.accessor}_web`}
                              className={`mx-auto w-full max-w-lg hidden xl:flex xl:max-w-none xl:w-full flex-col justify-center items-center`}
                            >
                              <input
                                type="text"
                                placeholder={`Search ${column.Header}`}
                                value={searchTerms[column.accessor]}
                                onChange={handleSearchChange(column.accessor)}
                                className="h-10 text-sm border rounded p-1 w-full md:w-full disabled:text-gray-500 disabled:cursor-not-allowed"
                                disabled={showMap}
                              />
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="md:col-span-1 lg:col-span-3 xl:col-span-3">
          <ListCards
            paginatedData={paginatedData}
            toggleDetailsVisibility={toggleDetailsVisibility}
            detailsVisibility={detailsVisibility}
            isImageAvailable={isImageAvailable}
            postcodeCoords={postcodeCoords}
            postcode={postcode}
            data={data}
            courseImages={courseImages}
            getPaginatedData={getPaginatedData}
            columns={columns}
            showMap={showMap}
            toggleView={toggleView}
            changePage={changePage}
            handleChangeCardsPerPage={handleChangeCardsPerPage}
            cardsPerPage={cardsPerPage}
            currentPage={currentPage}
            totalPages={totalPages}
          />
        </div>
      </div>
    </>
  );
}

export default FilterPane;
